import type { DropdownOption } from '../shared';

export const APP_NAME = 'iVentedBlast';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };

export const CASE_OPTIONS = [
  { value: 'Thru Wall', label: 'Thru Wall' },
  { value: 'Thru Roof', label: 'Thru Roof' },
  { value: '3-Wall (no roof)', label: '3-Wall (no roof)' },
  { value: '3-Wall (with roof)', label: '3-Wall (with roof)' }
] as const satisfies readonly DropdownOption[];

export type CaseType = (typeof CASE_OPTIONS)[number]['value'];

export interface APIPayload {
  icubicle: number;
  icubicle_conf: number;
  location: number;
  a: number;
  b: number;
  h: number;
  opening_b: number;
  opening_h: number;
  W: number;
  R: number;
  A: number;
  Pmax: number;
  Imax: number;
  iconfidential: boolean;
}

export interface APIResponse {
  room_volume: number;
  opening_area: number;
  ilocation: number;
  free_field: Record<string, number>;
  branch: string;
  outputs: Record<string, number>;
  inverse_ranges: Record<string, number> | null;
  text_panels: {
    primary: string[];
    results: string[];
  };
  charts: Record<string, { mime: string; base64: string }>;
}

export function getModeIndex(selectedCase: CaseType): number {
  return selectedCase === 'Thru Wall' || selectedCase === 'Thru Roof' ? 1 : 2;
}

export function getLocationIndex(selectedCase: CaseType): number {
  return selectedCase === 'Thru Roof' ? 2 : 1;
}

export function getFullyVentedIndex(selectedCase: CaseType, a: number, b: number, h: number): number {
  const isCube = Math.abs(a - b) < 1e-9 && Math.abs(b - h) < 1e-9;
  if (selectedCase === '3-Wall (no roof)') {
    return isCube ? 1 : 2;
  }
  if (selectedCase === '3-Wall (with roof)') {
    return isCube ? 3 : 4;
  }
  return 1;
}

export function formatBranchLabel(branch: string): string {
  if (branch === 'wall') return 'Partially Confined / Wall Opening';
  if (branch === 'roof') return 'Partially Confined / Roof Opening';
  if (branch === 'cube_three_wall_no_roof') return 'Fully Vented / Cubic Three Wall Without Roof';
  if (branch === 'rectangle_three_wall_no_roof') return 'Fully Vented / Rectangular Three Wall Without Roof';
  if (branch === 'cube_three_wall_with_roof') return 'Fully Vented / Cubic Three Wall With Roof';
  if (branch === 'rectangle_three_wall_with_roof') return 'Fully Vented / Rectangular Three Wall With Roof';
  return branch;
}

export function isPartiallyConfined(selectedCase: CaseType): boolean {
  return getModeIndex(selectedCase) === 1;
}

export function hasRoof(selectedCase: CaseType): boolean {
  return selectedCase !== '3-Wall (no roof)';
}

export function isRoofOpening(selectedCase: CaseType): boolean {
  return selectedCase === 'Thru Roof';
}

export function shouldShowOpeningInputs(selectedCase: CaseType): boolean {
  return isPartiallyConfined(selectedCase);
}

export function formatNumber(value: number | null | undefined, digits = 3): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(digits);
}
